import { AddressFragment, OrderDetailsFragment } from "@dashboard/graphql";
import { CSSProperties } from "react";
import { useIntl } from "react-intl";

import { isParcelShopDelivery } from "../../utils/delivery";
import { orderPrintMessages as messages } from "./messages";
import { getAddressLines, getAddressName, OrderPrintPaper, ShippingLabelFields } from "./utils";

interface ShippingLabelProps {
  order: OrderDetailsFragment;
  shopName: string;
  companyAddress: AddressFragment | null | undefined;
  pickupPointAddress: string;
  paper: OrderPrintPaper;
  printedAt: Date;
  fields: ShippingLabelFields;
}

// Printed output: plain black-on-white HTML sized in mm, independent of the
// dashboard theme.
const caption: CSSProperties = {
  fontSize: "0.8em",
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  marginBottom: "1.5mm",
};

const lines = (values: string[]) =>
  values.map((value, index) => <div key={`${index}-${value}`}>{value}</div>);

export const ShippingLabel = ({
  order,
  shopName,
  companyAddress,
  pickupPointAddress,
  paper,
  printedAt,
  fields,
}: ShippingLabelProps) => {
  const intl = useIntl();
  const isPickupPoint = isParcelShopDelivery(order.deliveryType);
  const address = order.shippingAddress;
  const homeCountryCode = companyAddress?.country?.code;
  const senderLines = getAddressLines(companyAddress, { withName: false, homeCountryCode });
  const senderName = companyAddress?.companyName?.trim() || shopName;
  const itemCount = order.lines.reduce((sum, line) => sum + line.quantity, 0);
  const isA4Half = paper.id === "a4-half";
  // Without a fixed label height (top of an A4 sheet) the label hugs its content.
  const hasFixedHeight = paper.contentHeightMm !== null;
  const showShippingMethod = fields.shippingMethod && !!order.shippingMethodName;
  const hasFooterLeft = fields.orderNumber || showShippingMethod;
  const hasFooterRight = fields.itemCount || fields.printedAt;

  return (
    <div
      style={{
        boxSizing: "border-box",
        width: `${paper.contentWidthMm}mm`,
        height: hasFixedHeight ? `${paper.contentHeightMm}mm` : undefined,
        maxHeight: paper.maxContentHeightMm ? `${paper.maxContentHeightMm}mm` : undefined,
        padding: hasFixedHeight ? "8mm" : "8mm 8mm 6mm",
        display: "flex",
        flexDirection: "column",
        gap: "5mm",
        fontFamily: "Helvetica, Arial, sans-serif",
        fontSize: `${10 * paper.fontScale}pt`,
        lineHeight: 1.3,
        color: "#000",
        background: "#fff",
        borderBottom: isA4Half ? "0.3mm dashed #000" : undefined,
        overflow: "hidden",
      }}
    >
      <div style={{ border: "0.3mm solid #000", padding: "3mm", flexShrink: 0 }}>
        <div style={caption}>{intl.formatMessage(messages.from)}</div>
        <div style={{ fontWeight: 700 }}>{senderName}</div>
        {lines(senderLines.filter(line => line !== senderName))}
        {companyAddress?.phone && (
          <div>{intl.formatMessage(messages.phone, { phone: companyAddress.phone })}</div>
        )}
      </div>

      <div
        style={{
          flex: hasFixedHeight ? 1 : undefined,
          // The only part that gives way when the label hits its maximum height,
          // so the sender and footer always print in full.
          flexShrink: 1,
          minHeight: 0,
          overflow: "hidden",
          border: "0.8mm solid #000",
          padding: "4mm 5mm",
          fontSize: "1.45em",
          lineHeight: 1.35,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "3mm" }}>
          <div style={{ ...caption, fontSize: "0.6em", marginBottom: 0 }}>
            {intl.formatMessage(messages.to)}
          </div>
          {isPickupPoint && (
            <div
              style={{
                ...caption,
                fontSize: "0.6em",
                marginBottom: 0,
                background: "#000",
                color: "#fff",
                padding: "0.6mm 2mm",
                WebkitPrintColorAdjust: "exact",
                printColorAdjust: "exact",
              }}
            >
              {intl.formatMessage(messages.pickupPointBadge)}
            </div>
          )}
        </div>
        <div style={{ height: "2mm" }} />
        {isPickupPoint ? (
          <>
            <div style={{ fontSize: "0.65em" }}>
              {intl.formatMessage(messages.pickupPointRecipient)}
            </div>
            <div style={{ fontWeight: 700 }}>{getAddressName(address)}</div>
            {address?.phone && (
              <div style={{ fontSize: "0.8em" }}>
                {intl.formatMessage(messages.phone, { phone: address.phone })}
              </div>
            )}
            <div style={{ height: "3mm" }} />
            <div style={{ fontSize: "0.65em" }}>
              {intl.formatMessage(messages.pickupPointAddress)}
            </div>
            <div style={{ whiteSpace: "pre-line", fontWeight: 700 }}>
              {pickupPointAddress.trim() || " "}
            </div>
          </>
        ) : (
          <>
            <div style={{ fontWeight: 700 }}>{getAddressName(address)}</div>
            {lines(getAddressLines(address, { withName: false, homeCountryCode }))}
            {address?.phone && (
              <div style={{ fontSize: "0.8em", marginTop: "2mm" }}>
                {intl.formatMessage(messages.phone, { phone: address.phone })}
              </div>
            )}
          </>
        )}
      </div>

      {(hasFooterLeft || hasFooterRight) && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "4mm",
            flexShrink: 0,
          }}
        >
          <div>
            {fields.orderNumber && (
              <div style={{ fontWeight: 700, fontSize: "1.3em" }}>
                {intl.formatMessage(messages.orderNumber, { number: order.number })}
              </div>
            )}
            {showShippingMethod && (
              <div>
                {intl.formatMessage(messages.shippingMethod, { method: order.shippingMethodName })}
              </div>
            )}
          </div>
          <div style={{ textAlign: "right", fontSize: "0.85em" }}>
            {fields.itemCount && (
              <div>{intl.formatMessage(messages.itemCount, { count: itemCount })}</div>
            )}
            {fields.printedAt && (
              <div>
                {intl.formatMessage(messages.printedAt, {
                  date: intl.formatDate(printedAt, {
                    dateStyle: "short",
                    timeStyle: "short",
                  } as Intl.DateTimeFormatOptions),
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
